// Top-right toast stack. Subscribes to notificationStore.toasts.
// Auto-dismisses on `expires` ts; respects prefers-reduced-motion (fade-only).

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ExternalLink, X } from 'lucide-react';
import { useNotificationStore, useToasts, severityFromEvent } from '@/store/notificationStore';
import { useAckEvent } from '@/hooks/useAlertEvents';
import { useApiMachines } from '@/hooks/useMachines';
import { useAuthStore } from '@/store/authStore';
import { machineDisplay } from '@/lib/machineDisplay';
import { resolveFieldLabel } from '@/sensors/labelResolver';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { AlertSeverity } from '@/types';

const SEV_COLOR: Record<AlertSeverity, string> = {
  fault:   '#EF4444',
  warning: '#F59E0B',
  info:    '#06B6D4',
  cleared: '#22C55E',
};

export function ToastStack() {
  const toasts  = useToasts();
  const events  = useNotificationStore((s) => s.events);
  const dismiss = useNotificationStore((s) => s.dismissToast);
  const ack     = useAckEvent();
  const nav     = useNavigate();
  const { data: api = [] } = useApiMachines();
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);

  // Auto-dismiss expired toasts.
  useEffect(() => {
    if (toasts.length === 0) return;
    const next = Math.min(...toasts.map((t) => t.expires)) - Date.now();
    const id = setTimeout(() => {
      const now = Date.now();
      for (const t of useNotificationStore.getState().toasts) {
        if (t.expires <= now) dismiss(t.id);
      }
    }, Math.max(50, next));
    return () => clearTimeout(id);
  }, [toasts, dismiss]);

  const hydrated = useMemo(() => toasts.map((t) => ({ toast: t, ev: events[t.eventId] })).filter((x) => x.ev), [toasts, events]);
  if (hydrated.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="alerts"
      className="fixed top-3 right-3 z-[100] flex flex-col gap-2 pointer-events-none"
    >
      {hydrated.map(({ toast, ev }) => {
        const sev = severityFromEvent(ev);
        const color = SEV_COLOR[sev];
        const dispMachine = machineDisplay(`${ev.company_slug}.${ev.machine_id}`, api, { isAdmin }).primary;
        return (
          <div
            key={toast.id}
            role="alert"
            className="pointer-events-auto w-[360px] rounded-lg border-2 bg-[var(--bg-raised)] shadow-[var(--shadow-xl)] motion-safe:animate-[slide-in-right_220ms_var(--ease-out)] motion-reduce:opacity-100"
            style={{ borderColor: color }}
          >
            <div className="px-3 py-2 flex items-center gap-2 border-b border-[var(--border-subtle)]">
              <Bell className="w-3.5 h-3.5" style={{ color }} />
              <span className="text-[10px] uppercase tracking-wider tabular font-medium" style={{ color }}>{sev}</span>
              <span className="font-display font-semibold text-sm truncate">{dispMachine}</span>
              <button
                onClick={() => dismiss(toast.id)}
                aria-label="dismiss"
                className="ml-auto p-1 rounded hover:bg-[var(--bg-elevated)] text-[var(--fg-muted)]"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="px-3 py-2 text-[11px] text-[var(--fg-muted)] font-display tabular">
              {resolveFieldLabel(ev.field)}
              {ev.operator && ev.threshold != null && <> {ev.operator} <span className="text-[var(--accent-warning)]">{ev.threshold}</span></>}
              {ev.value != null && <> · <span className="text-[var(--fg-primary)]">{fmt(ev.value)}</span></>}
            </div>
            <div className="px-3 pb-2 flex items-center gap-1">
              <button
                onClick={() => { ack.mutate({ id: ev.id }); dismiss(toast.id); }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-[#22C55E1A] border border-[#22C55E55] text-[var(--accent-healthy)] hover:bg-[#22C55E2A] transition-colors"
              >
                <Check className="w-3 h-3" /> ack
              </button>
              <button
                onClick={() => { dismiss(toast.id); nav(`/alerts?event=${encodeURIComponent(ev.id)}`); }}
                className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-[var(--border-strong)] text-[var(--fg-muted)] hover:text-[var(--fg-primary)] hover:border-[var(--accent-brand)] transition-colors')}
              >
                <ExternalLink className="w-3 h-3" /> view
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
